import type { MrfDashboardFilterTab } from './mrf-dashboard-state.service';

export type MrfViewAction = 'edit' | 'principal' | 'mis' | 'hr';

export type MrfDashboardRowTabStatus = Exclude<MrfDashboardFilterTab, 'ALL'>;

export const MRF_HRMS_FORM_GROUP = 'HRMS';

export const MRF_HRMS_APP_FORMS = {
  REQUEST: 'REQUEST_FORM',
  PRINCIPAL: 'PRINCIPAL_FORM',
  MIS: 'MIS_FORM',
  HR: 'HR_FORM',
  BUDGET: 'BUDGET_FORM',
  BUDGET_REPORT: 'BUDGET_REPORT',
} as const;

export interface MrfHrmsPermissions {
  requestForm: boolean;
  principalForm: boolean;
  misForm: boolean;
  hrForm: boolean;
  /** BUDGET_FORM present in HRMS roles — report and create. */
  budgetForm: boolean;
  /** BUDGET_REPORT present in HRMS roles — manpower budget report. */
  budgetReport: boolean;
}

export const EMPTY_MRF_HRMS_PERMISSIONS: MrfHrmsPermissions = {
  requestForm: false,
  principalForm: false,
  misForm: false,
  hrForm: false,
  budgetForm: false,
  budgetReport: false,
};

function isMrfAppFormFlagEnabled(value: unknown): boolean {
  return value === true || value === 'true' || value === 1 || value === '1';
}

const MRF_DATE_EMPTY = '__-__-____';

function toMrfDateParts(value: unknown): { day: string; month: string; year: string } | null {
  if (value == null) return null;

  if (typeof value === 'number') {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return {
      day: String(date.getDate()).padStart(2, '0'),
      month: String(date.getMonth() + 1).padStart(2, '0'),
      year: String(date.getFullYear()),
    };
  }

  const raw = String(value).trim();
  if (!raw) return null;

  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    return { day: iso[3], month: iso[2], year: iso[1] };
  }

  const dmy = raw.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/);
  if (dmy) {
    let year = dmy[3];
    if (year.length === 2) year = `20${year}`;
    return {
      day: dmy[1].padStart(2, '0'),
      month: dmy[2].padStart(2, '0'),
      year,
    };
  }

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return {
      day: String(parsed.getDate()).padStart(2, '0'),
      month: String(parsed.getMonth() + 1).padStart(2, '0'),
      year: String(parsed.getFullYear()),
    };
  }

  return null;
}

/** Display/API format: dd-mm-yyyy */
export function formatMrfDateForDisplay(
  value: unknown,
  emptyPlaceholder = MRF_DATE_EMPTY
): string {
  const parts = toMrfDateParts(value);
  if (!parts) {
    const raw = String(value ?? '').trim();
    return raw || emptyPlaceholder;
  }
  return `${parts.day}-${parts.month}-${parts.year}`;
}

/** API payload format: dd-mm-yyyy */
export function formatMrfDateForPayload(value: unknown): string {
  return formatMrfDateForDisplay(value, '');
}

/** HTML date input format: yyyy-MM-dd */
export function formatMrfDateForInput(value: unknown): string {
  const parts = toMrfDateParts(value);
  if (!parts) return '';
  return `${parts.year}-${parts.month}-${parts.day}`;
}

/** Read-only display with em dash when empty. */
export function formatMrfDateOrDash(value: unknown): string {
  return formatMrfDateForDisplay(value, '') || '—';
}

export interface MrfViewOption {
  id: MrfViewAction;
  label: string;
  path: string;
}

/** Last working date applies only when reason description is Exit. */
export function isMrfReasonExit(reasonDescription: unknown): boolean {
  return String(reasonDescription ?? '').trim().toLowerCase() === 'exit';
}

export function normalizeMrfWorkflowStatus(rawStatus: string): string {
  const status = String(rawStatus ?? '')
    .toUpperCase()
    .replace(/[\s-]+/g, '_');

  if (status.includes('REQUESTED')) return 'REQUESTED';
  if (status.includes('PENDING') && status.includes('MIS')) return 'PENDING_AT_MIS';
  if (status.includes('PENDING') && status.includes('HR')) return 'PENDING_AT_HR';
  if (status.includes('APPROVED') && status.includes('HR')) return 'APPROVED_AT_HR';
  if (status === 'APPROVED' || status.includes('APPROVED')) return 'APPROVED';
  if (status === 'REJECTED' || status.includes('REJECT')) return 'REJECTED';
  if (status === 'APPROVAL' || status.includes('APPROVAL')) return 'APPROVAL';
  if (status === 'PENDING' || status.includes('PEND')) return 'PENDING_AT_MIS';
  return status;
}

/** MRF Status header value — prefers API key `mrfStatus` from getRequests / getRequestsByHr. */
export function resolveMrfRequestStatusRaw(
  data: Record<string, unknown> | null | undefined
): string {
  if (!data) return '';
  return String(
    data['mrfStatus'] ??
      data['mrfFormStatus'] ??
      data['status'] ??
      data['formStatus'] ??
      data['statusName'] ??
      ''
  ).trim();
}

/** Onboarding Status header value — prefers API key `onboardingStatus`. */
export function resolveMrfOnboardingStatusRaw(
  data: Record<string, unknown> | null | undefined
): string {
  if (!data) return '';
  return String(
    data['onboardingStatus'] ??
      data['onBoardStatus'] ??
      data['onboardStatus'] ??
      data['onboardingStatusName'] ??
      data['onBoardStatusName'] ??
      data['employeeOnboardingStatus'] ??
      ''
  ).trim();
}

/** Human-readable label for the form header badge. */
export function formatMrfRequestStatusLabel(rawStatus: string): string {
  const raw = String(rawStatus ?? '').trim();
  if (!raw) return '';

  const workflow = normalizeMrfWorkflowStatus(raw);
  const known: Record<string, string> = {
    REQUESTED: 'Requested',
    PENDING_AT_MIS: 'Pending At MIS',
    PENDING_AT_HR: 'Pending At HR',
    APPROVED_AT_HR: 'Approved At HR',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
    APPROVAL: 'Approval',
  };
  if (known[workflow]) {
    return known[workflow];
  }

  return formatMrfStatusWords(raw);
}

export function formatMrfOnboardingStatusLabel(rawStatus: string): string {
  const raw = String(rawStatus ?? '').trim();
  if (!raw) return '';
  return formatMrfStatusWords(raw);
}

function formatMrfStatusWords(raw: string): string {
  return String(raw)
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

export type MrfRequestStatusTone =
  | 'requested'
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'default';

export function getMrfRequestStatusTone(rawStatus: string): MrfRequestStatusTone {
  const workflow = normalizeMrfWorkflowStatus(rawStatus);
  switch (workflow) {
    case 'APPROVED':
    case 'APPROVED_AT_HR':
      return 'approved';
    case 'REQUESTED':
      return 'requested';
    case 'PENDING_AT_MIS':
    case 'PENDING_AT_HR':
    case 'APPROVAL':
      return 'pending';
    case 'REJECTED':
      return 'rejected';
    default:
      return 'default';
  }
}

/** Color tone for onboarding status header (same palette as MRF status). */
export function getMrfOnboardingStatusTone(rawStatus: string): MrfRequestStatusTone {
  const status = String(rawStatus ?? '')
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
  if (!status) return 'default';

  if (
    status.includes('COMPLETE') ||
    status.includes('COMPLETED') ||
    status.includes('CONFIRM') ||
    status.includes('JOINED') ||
    status.includes('ONBOARDED') ||
    status.includes('SUCCESS') ||
    status.includes('DONE') ||
    status.includes('ACTIVE')
  ) {
    return 'approved';
  }
  if (
    status.includes('REJECT') ||
    status.includes('CANCEL') ||
    status.includes('FAIL') ||
    status.includes('HOLD') ||
    status.includes('STOP')
  ) {
    return 'rejected';
  }
  if (
    status.includes('PENDING') ||
    status.includes('IN_PROGRESS') ||
    status.includes('PROGRESS') ||
    status.includes('INITIAT') ||
    status.includes('PROCESS') ||
    status.includes('WAIT') ||
    status.includes('OPEN')
  ) {
    return 'pending';
  }
  if (
    status.includes('NOT_START') ||
    status.includes('YET_TO') ||
    status.includes('NEW') ||
    status.includes('DRAFT') ||
    status.includes('REQUEST')
  ) {
    return 'requested';
  }
  return 'default';
}

export function resolveMrfViewOptions(
  rawStatus: string,
  permissions?: MrfHrmsPermissions | null
): MrfViewOption[] {
  const options = resolveMrfViewOptionsForStatus(rawStatus);
  if (!permissions) {
    return options;
  }
  return filterMrfViewOptionsByPermissions(options, permissions);
}

export function parseMrfHrmsPermissions(res: unknown): MrfHrmsPermissions {
  const permissions: MrfHrmsPermissions = { ...EMPTY_MRF_HRMS_PERMISSIONS };
  if (!res || typeof res !== 'object') {
    return permissions;
  }

  const root = res as Record<string, unknown>;
  const nested =
    root['data'] && typeof root['data'] === 'object'
      ? (root['data'] as Record<string, unknown>)
      : root;
  const groups = nested['userAppForms'];
  if (!Array.isArray(groups)) {
    return permissions;
  }

  for (const group of groups) {
    if (!group || typeof group !== 'object') continue;
    const row = group as Record<string, unknown>;
    const formGroup = String(
      row['formGroup'] ?? row['formGroupName'] ?? row['groupName'] ?? ''
    )
      .trim()
      .toUpperCase();
    if (formGroup !== MRF_HRMS_FORM_GROUP) continue;

    const forms = row['appForms'] ?? row['forms'];
    if (!Array.isArray(forms)) continue;

    for (const form of forms) {
      if (!form || typeof form !== 'object') continue;
      const name = String(
        (form as Record<string, unknown>)['appFormName'] ??
          (form as Record<string, unknown>)['formName'] ??
          ''
      )
        .trim()
        .toUpperCase();

      const formRow = form as Record<string, unknown>;
      if (name === MRF_HRMS_APP_FORMS.REQUEST) permissions.requestForm = true;
      if (name === MRF_HRMS_APP_FORMS.PRINCIPAL) permissions.principalForm = true;
      if (name === MRF_HRMS_APP_FORMS.MIS) permissions.misForm = true;
      if (name === MRF_HRMS_APP_FORMS.HR) permissions.hrForm = true;
      if (name === MRF_HRMS_APP_FORMS.BUDGET) {
        permissions.budgetForm = true;
      }
      if (name === MRF_HRMS_APP_FORMS.BUDGET_REPORT) {
        permissions.budgetReport = true;
      }
    }
  }

  return permissions;
}

export function canAccessBudgetForm(permissions: MrfHrmsPermissions): boolean {
  return permissions.budgetForm;
}

export function canAccessHrmsBudgetReport(permissions: MrfHrmsPermissions): boolean {
  return permissions.budgetReport;
}

/** Create / update budget form is turned off; report stays available. */
export const BUDGET_FORM_ENABLED = true;

/** Employee Withdrawal icon in the MRF sidebar is turned off. */
export const EMPLOYEE_WITHDRAWAL_NAV_ENABLED = false;

/** Alias — report and create share the same BUDGET_FORM role. */
export function canAccessBudgetReport(permissions: MrfHrmsPermissions): boolean {
  return canAccessBudgetForm(permissions);
}

export function filterMrfViewOptionsByPermissions(
  options: MrfViewOption[],
  permissions: MrfHrmsPermissions
): MrfViewOption[] {
  const access: Record<MrfViewAction, boolean> = {
    edit: permissions.requestForm,
    principal: permissions.principalForm,
    mis: permissions.misForm,
    hr: permissions.hrForm,
  };
  return options.filter((option) => access[option.id]);
}

export function hasMrfDashboardAccess(permissions: MrfHrmsPermissions): boolean {
  return (
    permissions.requestForm ||
    permissions.principalForm ||
    permissions.misForm ||
    permissions.hrForm
  );
}

export function canAccessMrfDashboardTab(
  tab: MrfDashboardFilterTab,
  permissions: MrfHrmsPermissions
): boolean {
  if (!hasMrfDashboardAccess(permissions)) {
    return false;
  }
  switch (tab) {
    case 'ALL':
      return true;
    case 'REQUESTED':
      return permissions.requestForm || permissions.principalForm;
    case 'PENDING':
      return permissions.principalForm || permissions.misForm || permissions.hrForm;
    case 'APPROVED':
    case 'REJECTED':
      return true;
    default:
      return false;
  }
}

export function getVisibleMrfDashboardTabs(
  permissions: MrfHrmsPermissions
): MrfDashboardFilterTab[] {
  const tabs: MrfDashboardFilterTab[] = ['ALL', 'REQUESTED', 'PENDING', 'APPROVED', 'REJECTED'];
  return tabs.filter((tab) => canAccessMrfDashboardTab(tab, permissions));
}

export function getAllowedMrfDashboardRowTabStatuses(
  permissions: MrfHrmsPermissions
): Set<MrfDashboardRowTabStatus> {
  const rowTabs: MrfDashboardRowTabStatus[] = ['REQUESTED', 'PENDING', 'APPROVED', 'REJECTED'];
  return new Set(rowTabs.filter((tab) => canAccessMrfDashboardTab(tab, permissions)));
}

function resolveMrfViewOptionsForStatus(rawStatus: string): MrfViewOption[] {
  const workflow = normalizeMrfWorkflowStatus(rawStatus);
  const all: Record<MrfViewAction, MrfViewOption> = {
    edit: {
      id: 'edit',
      label: 'Edit',
      path: '/hrDetails/manPowerRequisitionForm',
    },
    principal: {
      id: 'principal',
      label: 'Principal Approval',
      path: '/hrDetails/mrfPrincipalDean',
    },
    mis: {
      id: 'mis',
      label: 'MIS Approval',
      path: '/hrDetails/mrfUser2',
    },
    hr: {
      id: 'hr',
      label: 'HR Approval',
      path: '/hrDetails/mrfHrForm',
    },
  };

  switch (workflow) {
    case 'REQUESTED':
      return [all.edit, all.principal];
    case 'PENDING_AT_MIS':
      return [all.principal, all.mis];
    case 'PENDING_AT_HR':
      return [all.mis, all.hr];
    case 'APPROVED_AT_HR':
      return [all.hr];
    case 'APPROVAL':
    case 'APPROVED':
      return [all.hr];
    case 'REJECTED':
      return [all.hr];
    default:
      return [all.principal];
  }
}

export const MRF_AADHAAR_DIGIT_LENGTH = 12;
export const MRF_MOBILE_DIGIT_LENGTH = 10;
export const MRF_MAX_UPLOAD_FILE_BYTES = 2 * 1024 * 1024;
export const MRF_MAX_UPLOAD_FILE_SIZE_LABEL = '2 MB';

export function isMrfUploadFileWithinSizeLimit(file: File): boolean {
  return file.size <= MRF_MAX_UPLOAD_FILE_BYTES;
}

export function getMrfOversizedUploadFiles(files: File[]): File[] {
  return files.filter((file) => !isMrfUploadFileWithinSizeLimit(file));
}

export function getMrfUploadFileSizeValidationMessage(files: File[]): string | null {
  const oversized = getMrfOversizedUploadFiles(files);
  if (!oversized.length) {
    return null;
  }
  if (oversized.length === 1) {
    return `${oversized[0].name} exceeds the ${MRF_MAX_UPLOAD_FILE_SIZE_LABEL} upload limit.`;
  }
  return `${oversized.length} files exceed the ${MRF_MAX_UPLOAD_FILE_SIZE_LABEL} upload limit.`;
}

export function filterMrfUploadFilesBySize(files: File[]): File[] {
  return files.filter(isMrfUploadFileWithinSizeLimit);
}

export type MrfUploadErrorMap = Record<string, string>;

export function setMrfUploadSizeError(
  errors: MrfUploadErrorMap,
  slot: string,
  files: File[]
): string | null {
  const message = getMrfUploadFileSizeValidationMessage(files);
  if (message) {
    errors[slot] = message;
    return message;
  }
  delete errors[slot];
  return null;
}

export function clearMrfUploadSizeError(errors: MrfUploadErrorMap, slot: string): void {
  delete errors[slot];
}

export function getMrfUploadSizeError(errors: MrfUploadErrorMap, slot: string): string {
  return errors[slot] ?? '';
}

export function normalizeMrfMobileDigits(value: unknown): string {
  return String(value ?? '').replace(/\D/g, '').slice(0, MRF_MOBILE_DIGIT_LENGTH);
}

export function isValidMrfMobileNo(value: unknown): boolean {
  return normalizeMrfMobileDigits(value).length === MRF_MOBILE_DIGIT_LENGTH;
}

export function getMrfMobileValidationError(
  value: unknown,
  fieldLabel = 'Mobile No.'
): string | null {
  const digits = normalizeMrfMobileDigits(value);
  if (!digits) {
    return `Please enter ${fieldLabel}.`;
  }
  if (digits.length !== MRF_MOBILE_DIGIT_LENGTH) {
    return `${fieldLabel} must be exactly ${MRF_MOBILE_DIGIT_LENGTH} digits.`;
  }
  return null;
}

/** Keeps mobile inputs numeric-only and capped at 10 digits. */
export function syncMrfMobileControlFromInput(
  control: { value: unknown; setValue: (value: string, options?: { emitEvent?: boolean }) => void },
  event: Event
): void {
  const input = event.target as HTMLInputElement | null;
  if (!input) {
    return;
  }
  const digits = normalizeMrfMobileDigits(input.value);
  if (input.value !== digits) {
    input.value = digits;
  }
  if (String(control.value ?? '') !== digits) {
    control.setValue(digits, { emitEvent: true });
  }
}

export function normalizeMrfAadhaarDigits(value: unknown): string {
  return String(value ?? '').replace(/\D/g, '');
}

/** Aadhaar: 12 digits, first digit 2–9 (UIDAI format). */
export const MRF_AADHAAR_REGEX = /^[2-9]\d{11}$/;

/** Verhoeff multiplication table. */
const MRF_VERHOEFF_D = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 2, 3, 4, 0, 6, 7, 8, 9, 5],
  [2, 3, 4, 0, 1, 7, 8, 9, 5, 6],
  [3, 4, 0, 1, 2, 8, 9, 5, 6, 7],
  [4, 0, 1, 2, 3, 9, 5, 6, 7, 8],
  [5, 9, 8, 7, 6, 0, 4, 3, 2, 1],
  [6, 5, 9, 8, 7, 1, 0, 4, 3, 2],
  [7, 6, 5, 9, 8, 2, 1, 0, 4, 3],
  [8, 7, 6, 5, 9, 3, 2, 1, 0, 4],
  [9, 8, 7, 6, 5, 4, 3, 2, 1, 0],
];

/** Verhoeff permutation table. */
const MRF_VERHOEFF_P = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 5, 7, 6, 2, 8, 3, 0, 9, 4],
  [5, 8, 0, 3, 7, 9, 6, 1, 4, 2],
  [8, 9, 1, 6, 0, 4, 3, 5, 2, 7],
  [9, 4, 5, 3, 1, 2, 6, 8, 7, 0],
  [4, 2, 8, 6, 5, 7, 3, 9, 0, 1],
  [2, 7, 9, 3, 8, 0, 6, 4, 1, 5],
  [7, 0, 4, 6, 9, 1, 3, 2, 5, 8],
];

/** Verhoeff checksum for Aadhaar (true when check digit is valid). */
export function isValidMrfAadhaarVerhoeff(digits: string): boolean {
  if (!/^\d+$/.test(digits)) {
    return false;
  }
  let c = 0;
  const reversed = digits.split('').reverse();
  for (let i = 0; i < reversed.length; i++) {
    const digit = Number(reversed[i]);
    c = MRF_VERHOEFF_D[c][MRF_VERHOEFF_P[i % 8][digit]];
  }
  return c === 0;
}

/**
 * Client-side Aadhaar verification: regex format + Verhoeff checksum.
 * Returns null when valid; otherwise an error message.
 */
export function validateMrfAadhaarNo(
  value: unknown,
  fieldLabel = 'Aadhaar No.'
): string | null {
  const digits = normalizeMrfAadhaarDigits(value);
  if (!digits) {
    return `Please enter ${fieldLabel}.`;
  }
  if (digits.length !== MRF_AADHAAR_DIGIT_LENGTH || !MRF_AADHAAR_REGEX.test(digits)) {
    return `${fieldLabel} must be a valid 12-digit Aadhaar number.`;
  }
  if (!isValidMrfAadhaarVerhoeff(digits)) {
    return `Please enter a valid ${fieldLabel}.`;
  }
  return null;
}

/** Whether getRequests / MRF form data marks this request as rehiring. */
export function isMrfRehiringFromData(
  data: Record<string, unknown> | null | undefined
): boolean {
  if (!data) return false;
  const value = data['isReHiring'] ?? data['isRehiring'] ?? data['rehiring'];
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value == null) return false;
  const normalized = String(value).trim().toLowerCase();
  return normalized === 'true' || normalized === 'yes' || normalized === 'y' || normalized === '1';
}

/** Exact rehiring aadhaar saved on the MRF form (12 digits, no masking). */
export function resolveMrfRehiringAadharNo(data: Record<string, unknown>): string {
  for (const key of [
    'aadharNo',
    'aadhaarNo',
    'aadhar',
    'aadhaar',
    'aadharNumber',
    'aadharCardNo',
    'aadhaarNumber',
  ]) {
    const val = data[key];
    if (val != null && String(val).trim() !== '' && String(val) !== '0') {
      return normalizeMrfAadhaarDigits(val);
    }
  }
  return '';
}

/** Prefer saved MRF rehiring aadhaar over employee-search values (often masked/partial). */
export function resolveMrfRehiringAadharPreferringSaved(
  mrfData: Record<string, unknown>,
  searchAadhar?: unknown
): string {
  const saved = resolveMrfRehiringAadharNo(mrfData);
  if (saved.length === MRF_AADHAAR_DIGIT_LENGTH) {
    return saved;
  }
  const fromSearch = normalizeMrfAadhaarDigits(searchAadhar);
  return fromSearch || saved;
}

export function resolveMrfTempPayrollId(data: Record<string, unknown>): string {
  for (const key of ['tempPayrollId', 'employeeTempId', 'empTempId', 'tempEmpId']) {
    const val = data[key];
    if (val != null && String(val).trim() !== '' && String(val) !== '0') {
      return String(val).trim();
    }
  }
  return '';
}

export function resolveMrfSurname(data: Record<string, unknown>): string {
  for (const key of ['surname', 'employeeSurname', 'lastName']) {
    const val = data[key];
    if (val != null && String(val).trim() !== '' && String(val) !== '0') {
      return String(val).trim();
    }
  }
  return '';
}

export function isValidMrfAadhaarNo(value: unknown): boolean {
  return validateMrfAadhaarNo(value) == null;
}

export function getMrfAadhaarValidationError(
  value: unknown,
  fieldLabel = 'Aadhaar No.'
): string | null {
  return validateMrfAadhaarNo(value, fieldLabel);
}

/** Keeps Aadhaar inputs numeric-only (strips letters/symbols on input). */
export function syncMrfAadhaarControlFromInput(
  control: { value: unknown; setValue: (value: string, options?: { emitEvent?: boolean }) => void },
  event: Event
): void {
  const input = event.target as HTMLInputElement | null;
  if (!input) {
    return;
  }
  const digits = normalizeMrfAadhaarDigits(input.value);
  if (input.value !== digits) {
    input.value = digits;
  }
  if (String(control.value ?? '') !== digits) {
    control.setValue(digits, { emitEvent: true });
  }
}

export const MRF_PAN_LENGTH = 10;

/** PAN: 5 letters + 4 digits + 1 letter; 4th char is taxpayer entity type. */
export const MRF_PAN_ENTITY_TYPE_CHARS = 'CPHAFTBLJG';

export const MRF_PAN_REGEX = new RegExp(
  `^[A-Z]{3}[${MRF_PAN_ENTITY_TYPE_CHARS}][A-Z][0-9]{4}[A-Z]$`
);

export function normalizeMrfPanNo(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
}

/** Validates PAN 4th character (entity type code per Income Tax PAN structure). */
export function isValidMrfPanEntityType(value: string): boolean {
  if (value.length < 4) {
    return false;
  }
  return MRF_PAN_ENTITY_TYPE_CHARS.includes(value[3]);
}

/**
 * Client-side PAN verification: regex format + entity-type algorithm.
 * Returns null when valid; otherwise an error message.
 */
export function validateMrfPanNo(value: unknown, fieldLabel = 'Pan Card No.'): string | null {
  const pan = normalizeMrfPanNo(value);
  if (!pan) {
    return `Please enter ${fieldLabel}.`;
  }
  if (pan.length !== MRF_PAN_LENGTH || !MRF_PAN_REGEX.test(pan)) {
    return `${fieldLabel} must be a valid 10-character PAN (e.g. ABCPD1234E).`;
  }
  if (!isValidMrfPanEntityType(pan)) {
    return `Please enter a valid ${fieldLabel}.`;
  }
  return null;
}

export function isValidMrfPanNo(value: unknown): boolean {
  return validateMrfPanNo(value) == null;
}

export function getMrfPanValidationError(
  value: unknown,
  fieldLabel = 'Pan Card No.'
): string | null {
  return validateMrfPanNo(value, fieldLabel);
}

/** Keeps PAN inputs uppercase alphanumeric-only (max 10 chars). */
export function syncMrfPanControlFromInput(
  control: { value: unknown; setValue: (value: string, options?: { emitEvent?: boolean }) => void },
  event: Event
): void {
  const input = event.target as HTMLInputElement | null;
  if (!input) {
    return;
  }
  const pan = normalizeMrfPanNo(input.value).slice(0, MRF_PAN_LENGTH);
  if (input.value !== pan) {
    input.value = pan;
  }
  if (String(control.value ?? '') !== pan) {
    control.setValue(pan, { emitEvent: true });
  }
}

export const MRF_EXPECTED_SALARY_MAX_DIGITS = 7;

export function normalizeMrfAmountParts(
  value: unknown,
  allowNegative = false
): { sign: string; digits: string } {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return {
      sign: allowNegative && value < 0 ? '-' : '',
      digits: String(Math.trunc(Math.abs(value))),
    };
  }
  const raw = String(value ?? '').trim();
  const sign = allowNegative && raw.startsWith('-') ? '-' : '';
  return {
    sign,
    digits: raw.replace(/\D/g, ''),
  };
}

export function normalizeMrfAmountDigits(value: unknown): string {
  return normalizeMrfAmountParts(value).digits;
}

export function formatMrfAmountWithCommas(value: unknown, allowNegative = false): string {
  const { sign, digits } = normalizeMrfAmountParts(value, allowNegative);
  if (!digits) {
    return allowNegative && sign ? '-' : '';
  }
  const amount = Number(digits);
  if (Number.isNaN(amount)) {
    return allowNegative && sign ? '-' : '';
  }
  return `${sign}${amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

export function formatMrfAmountForDisplay(value: unknown, emptyFallback = '—'): string {
  const formatted = formatMrfAmountWithCommas(value);
  return formatted || emptyFallback;
}

export function parseMrfAmountNumber(value: unknown, allowNegative = false): number | null {
  const { sign, digits } = normalizeMrfAmountParts(value, allowNegative);
  if (!digits) {
    return null;
  }
  const amount = Number(digits);
  if (Number.isNaN(amount)) {
    return null;
  }
  return sign ? -amount : amount;
}

export function syncMrfAmountControlFromInput(
  control: { value: unknown; setValue: (value: string, options?: { emitEvent?: boolean }) => void },
  event: Event,
  maxDigits?: number,
  allowNegative = false
): void {
  const input = event.target as HTMLInputElement | null;
  if (!input) {
    return;
  }
  const parts = normalizeMrfAmountParts(input.value, allowNegative);
  if (maxDigits != null) {
    parts.digits = parts.digits.slice(0, maxDigits);
  }
  const formatted = formatMrfAmountWithCommas(
    `${parts.sign}${parts.digits}`,
    allowNegative
  );
  if (input.value !== formatted) {
    input.value = formatted;
  }
  if (String(control.value ?? '') !== formatted) {
    control.setValue(formatted, { emitEvent: true });
  }
}

export function normalizeMrfExpectedSalaryDigits(value: unknown): string {
  return normalizeMrfAmountDigits(value).slice(0, MRF_EXPECTED_SALARY_MAX_DIGITS);
}

export function getMrfExpectedSalaryValidationError(
  value: unknown,
  fieldLabel = 'Expected Salary'
): string | null {
  const digits = normalizeMrfExpectedSalaryDigits(value);
  if (!digits) {
    return `Please enter ${fieldLabel}.`;
  }
  return null;
}

/** Keeps expected salary numeric-only, capped at 7 digits, with comma grouping. */
export function syncMrfExpectedSalaryControlFromInput(
  control: { value: unknown; setValue: (value: string, options?: { emitEvent?: boolean }) => void },
  event: Event
): void {
  syncMrfAmountControlFromInput(control, event, MRF_EXPECTED_SALARY_MAX_DIGITS);
}

export const MRF_WORKLOAD_MAX = 8;

/** WorkLoad per day: digits only, capped at 8 hours. */
export function normalizeMrfWorkloadValue(value: unknown): string {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (!digits) {
    return '';
  }
  const num = Number(digits);
  if (!Number.isFinite(num) || num <= 0) {
    return '';
  }
  return String(Math.min(num, MRF_WORKLOAD_MAX));
}

export function getMrfWorkloadValidationError(
  value: unknown,
  fieldLabel = 'WorkLoad per day'
): string | null {
  const normalized = normalizeMrfWorkloadValue(value);
  if (!normalized) {
    return `Please enter ${fieldLabel}.`;
  }
  const num = Number(normalized);
  if (num > MRF_WORKLOAD_MAX) {
    return `${fieldLabel} cannot be more than ${MRF_WORKLOAD_MAX}.`;
  }
  return null;
}

/** Keeps workload inputs numeric-only and capped at 8. */
export function syncMrfWorkloadControlFromInput(
  control: { value: unknown; setValue: (value: string, options?: { emitEvent?: boolean }) => void },
  event: Event
): void {
  const input = event.target as HTMLInputElement | null;
  if (!input) {
    return;
  }
  const normalized = normalizeMrfWorkloadValue(input.value);
  if (input.value !== normalized) {
    input.value = normalized;
  }
  if (String(control.value ?? '') !== normalized) {
    control.setValue(normalized, { emitEvent: true });
  }
}

/** Maps API gender codes (e.g. MALE) to form option values (Male, Female, Other). */
export function normalizeMrfGenderValue(value: unknown): string {
  const text = String(value ?? '').trim();
  if (!text) {
    return '';
  }

  const upper = text.toUpperCase();
  if (upper === 'MALE' || upper === 'M') {
    return 'Male';
  }
  if (upper === 'FEMALE' || upper === 'F') {
    return 'Female';
  }
  if (upper === 'OTHER' || upper === 'O') {
    return 'Other';
  }

  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

export function resolveMrfYearsOfExperience(data: Record<string, unknown>): string {
  for (const key of ['yearsOfExp', 'desiredExperience', 'experience', 'desiredYearsOfExp']) {
    const val = data[key];
    if (val != null && String(val).trim() !== '') {
      return String(val).trim();
    }
  }
  return '';
}

export const MRF_YEARS_OF_EXPERIENCE_MAX = 58;

/**
 * Years of experience: digits + optional one decimal (e.g. 1.5), capped at 58.
 * Allows a trailing "." while typing (e.g. "1.").
 */
export function normalizeMrfYearsOfExperienceValue(value: unknown): string {
  let text = String(value ?? '').replace(/[^\d.]/g, '');
  if (!text) {
    return '';
  }

  const firstDot = text.indexOf('.');
  if (firstDot !== -1) {
    text =
      text.slice(0, firstDot + 1) + text.slice(firstDot + 1).replace(/\./g, '').slice(0, 1);
  }

  if (text === '.') {
    return '0.';
  }

  if (text.endsWith('.')) {
    const whole = Number(text.slice(0, -1));
    if (!Number.isFinite(whole) || whole < 0) {
      return '';
    }
    if (whole > MRF_YEARS_OF_EXPERIENCE_MAX) {
      return String(MRF_YEARS_OF_EXPERIENCE_MAX);
    }
    return `${whole}.`;
  }

  const num = Number(text);
  if (!Number.isFinite(num) || num < 0) {
    return '';
  }
  if (num > MRF_YEARS_OF_EXPERIENCE_MAX) {
    return String(MRF_YEARS_OF_EXPERIENCE_MAX);
  }
  return text;
}

export function getMrfYearsOfExperienceValidationError(
  value: unknown,
  fieldLabel = 'Years of Experience'
): string | null {
  const raw = String(value ?? '').trim();
  if (!raw) {
    return `Please enter ${fieldLabel}.`;
  }

  if (!/^\d+(\.\d)?$/.test(raw)) {
    return `${fieldLabel} must be a number (e.g. 1 or 1.5).`;
  }

  const num = Number(raw);
  if (!Number.isFinite(num) || num < 0) {
    return `${fieldLabel} must be a valid number.`;
  }
  if (num > MRF_YEARS_OF_EXPERIENCE_MAX) {
    return `${fieldLabel} cannot be more than ${MRF_YEARS_OF_EXPERIENCE_MAX}.`;
  }
  return null;
}

/** Keeps years-of-experience numeric (optional 1 decimal) and capped at 58. */
export function syncMrfYearsOfExperienceControlFromInput(
  control: { value: unknown; setValue: (value: string, options?: { emitEvent?: boolean }) => void },
  event: Event
): void {
  const input = event.target as HTMLInputElement | null;
  if (!input) {
    return;
  }
  const normalized = normalizeMrfYearsOfExperienceValue(input.value);
  if (input.value !== normalized) {
    input.value = normalized;
  }
  if (String(control.value ?? '') !== normalized) {
    control.setValue(normalized, { emitEvent: true });
  }
}

/** Qualified employee name — excludes auto-copied rehiring/replacement names when exam was not taken. */
export function resolveMrfQualifiedEmployeeName(
  data: Record<string, unknown>,
  examTaken: boolean
): string {
  const pick = (...keys: string[]): string => {
    for (const key of keys) {
      const val = data[key];
      if (val != null && String(val).trim() !== '' && String(val) !== '0') {
        return String(val).trim();
      }
    }
    return '';
  };

  const name = pick('newEmpName', 'newEmployeeName', 'candidateName', 'employeeName');
  if (!name) {
    return '';
  }
  if (examTaken) {
    return name;
  }

  const prevName = pick('prevEmpName');
  const replName = pick('replacementEmpName');
  if ((prevName && name === prevName) || (replName && name === replName)) {
    return '';
  }
  return name;
}
